import { skipToken } from "@reduxjs/toolkit/query";
import { getCompanyTimezone } from "@repo/app-types/entities/company";
import { checkPlaybookHasKnowledgeBaseAdherence } from "@repo/app-types/entities/playbook";
import { TeammateType } from "@repo/app-types/entities/teammate";
import { asGradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconRefresh } from "@tabler/icons-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { Header, Heading } from "react-aria-components";
import { useParams } from "react-router";
import {
  usePlaybookGradingQuery,
  usePlaybookGradingReassessMutation,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useMyRecordingAssessmentFeedbackQuery } from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { selectCompany } from "../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { CallActivityEmptyState } from "./callActivityEmptyState.js";
import { GradingAssessmentTable } from "./gradingAssessmentTable.js";
import { GradingCustomerSidebar } from "./gradingCustomerSidebar.js";
import { GradingScheduledCallBanner } from "./gradingScheduledCallBanner.js";
import { IncorrectStatementsIndication } from "./incorrectStatementsIndication.js";
import { KnowledgeBaseAdherenceStatementsCardContainer } from "./knowledgeBaseAdherenceStatementsCardContainer.js";
import { ToolUsageAccuracyCardContainer } from "./toolUsageAccuracyCardContainer.js";

/**
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/customer-interactions/<gradingId>/assessment
 */
export const TrainingTeammatePageOverviewGradingAssessment: FC = () => {
  const { gradingId: gradingIdParam } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });

  // teammate/account/playbook are derived from the grading; the route only
  // carries the grading id.
  const teammateId = grading?.teammate_id;
  const playbookId = grading?.playbook_id;

  const { data: teammate } = useBasicTeammateQuery(teammateId ?? skipToken);

  const assessment = grading?.assessment;
  const callActivity = grading?.call_activity;
  const firstContact = grading?.contacts.at(0);
  const firstRecording = grading?.recordings.at(0);
  const companyTimezone = getCompanyTimezone(useAppSelector(selectCompany));

  const [reassessGrading, { isLoading: isReassessing }] =
    usePlaybookGradingReassessMutation();
  const [justUpdated, setJustUpdated] = useState(false);

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  const { data: feedback } = useMyRecordingAssessmentFeedbackQuery(
    firstRecording?.id && playbook?.id
      ? {
          recordingId: firstRecording.id,
          playbookId: playbook.id,
        }
      : skipToken,
  );

  // Hide the "Updated" indicator after 3 seconds
  useEffect(() => {
    if (!justUpdated) {
      return;
    }

    const timer = setTimeout(() => setJustUpdated(false), 3000);
    return () => clearTimeout(timer);
  }, [justUpdated]);

  const dispatch = useAppDispatch();
  const handleReassess = async () => {
    if (!grading) {
      return;
    }

    try {
      await reassessGrading({ gradingId }).unwrap();
      setJustUpdated(true);
    } catch (error) {
      showNotification(dispatch, {
        id: `reassess-error-${grading.id}-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to regrade recording. Please try again.",
      });
    }
  };

  const ownTeammate = useAppSelector(selectTeammate) || undefined;
  const maybeRenderReassessButton = () => {
    if (!isAlpharunEmail(ownTeammate?.email)) {
      return null;
    }

    return (
      <div className="flex shrink-0 items-center gap-2">
        {justUpdated && (
          <span className="animate-pulse rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
            Updated
          </span>
        )}
        <Button
          variant={ButtonVariant.Tertiary}
          size={Size.MEDIUM}
          onClick={handleReassess}
          isDisabled={isReassessing}
        >
          {isReassessing ? (
            <div className="flex flex-1 items-center pt-px">
              <Loader style={LoaderStyle.ORBIT} />
            </div>
          ) : (
            <>
              <IconRefresh className="size-4" />
              <VisuallyHidden>Reassess</VisuallyHidden>
            </>
          )}
        </Button>
      </div>
    );
  };

  // Voice agents are graded on their own playbook, so the playbook adherence
  // results aren't meaningful for them — hide that section for agent calls.
  const isVoiceAgent = teammate?.type === TeammateType.AGENT;

  return (
    <>
      <div className="flex h-full">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {callActivity?.next_action && grading ? (
              <GradingScheduledCallBanner
                nextAction={callActivity.next_action}
                gradingId={grading.id}
                teammateAgentId={grading.teammate_id}
                customerName={firstContact?.first_name ?? undefined}
                timezone={companyTimezone}
              />
            ) : null}

            {/* Only when the tab would otherwise hold nothing but the banner
                above — everything else here hangs off the assessment. */}
            {!assessment ? (
              <CallActivityEmptyState
                callActivity={callActivity}
                surface="overview"
              />
            ) : null}

            {assessment ? (
              <>
                {grading.tool_usage_accuracy ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Tool usage accuracy
                      </Heading>
                    </Header>

                    <ToolUsageAccuracyCardContainer
                      data={grading.tool_usage_accuracy}
                      gradingId={gradingId}
                    />
                  </section>
                ) : null}

                {!isVoiceAgent ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        {playbook?.name ?? "Playbook adherence"}
                      </Heading>
                    </Header>

                    {playbook && firstRecording && (
                      <GradingAssessmentTable
                        assessment={assessment}
                        recordingId={firstRecording.id}
                        playbook={playbook}
                        feedback={feedback}
                      />
                    )}
                  </section>
                ) : null}

                {playbook !== undefined &&
                checkPlaybookHasKnowledgeBaseAdherence(playbook) ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Knowledge base adherence
                      </Heading>

                      <IncorrectStatementsIndication
                        isVisibleWhenZero
                        knowledgeBaseAdherence={
                          grading.knowledge_base_adherence
                        }
                      />
                    </Header>

                    <KnowledgeBaseAdherenceStatementsCardContainer
                      recordings={grading.recordings}
                      knowledgeBaseAdherence={grading.knowledge_base_adherence}
                    />
                  </section>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {grading && (
          <GradingCustomerSidebar
            grading={grading}
            contact={firstContact}
            teammate={teammate}
            playbook={playbook}
            isGradingLoading={isGradingLoading}
            reassessControl={assessment ? maybeRenderReassessButton() : null}
          />
        )}
      </div>
      <NavigationSlideoverContainer />
    </>
  );
};
